import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { toast } from "../../utils/toast";
import { CalendarClock, X, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { API_URL } from "../../api/api";
import { hasPermission } from "../../utils/permissionUtils";
import { T, Label, Input, Select, PrimaryButton, SecondaryButton } from "../../components/em/ui";

const auth = () => {
  const t = localStorage.getItem("token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

// Vertical rhythm shared by every field in this form.
const field = { marginBottom: 18 };

// Local YYYY-MM / YYYY-MM-DD. toISOString() would roll back a day (or a month)
// for any timezone ahead of UTC.
const pad = (n) => String(n).padStart(2, "0");
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const dayKey = (d) => `${monthKey(d)}-${pad(d.getDate())}`;
// Chronological, with a hand-added day that has no date yet kept at the end.
const byDate = (a, b) => {
  if (!a.date || !b.date) return (a.date ? 0 : 1) - (b.date ? 0 : 1);
  return a.date.localeCompare(b.date);
};

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
};
const shiftMonth = (ym, by) => {
  const [y, m] = ym.split("-").map(Number);
  return monthKey(new Date(y, m - 1 + by, 1));
};
const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

// Why each day was flagged, in the employee's words rather than the API's.
const issueLabel = (row) => {
  switch (row.issue) {
    case "no_record":
      return "No record";
    case "missing_out":
      return "Missing check-out";
    case "late_in":
      return `Checked in late — ${row.recorded_in || ""}`.trim();
    default:
      return "Added manually";
  }
};

const linkBtn = {
  border: "none",
  background: "none",
  padding: 0,
  cursor: "pointer",
  font: `600 12.5px ${T.font}`,
  color: T.orange,
};

const RegularisationModal = ({ onClose, onSuccess, employees = [] }) => {
  const userData = (() => {
    try { return JSON.parse(localStorage.getItem("userdata") || "{}"); }
    catch { return {}; }
  })();
  const myId = userData.id || localStorage.getItem("user_id");
  // A `manage_attendance` holder can submit on behalf of an employee — show
  // the picker. Same key the server checks in submitRegularisation.
  const isManager = hasPermission("manage_attendance");

  const [month, setMonth] = useState(monthKey(new Date()));
  const [rows, setRows] = useState([]);
  const [shift, setShift] = useState({ start: "09:30", end: "18:30" });
  // Dates in the shown month with a regularisation still awaiting a decision —
  // the only dates that can't be raised again. Approved or rejected ones can.
  const [pendingDates, setPendingDates] = useState(() => new Set());
  const [loadingDays, setLoadingDays] = useState(true);

  const [userId, setUserId] = useState(myId || "");
  const [managerId, setManagerId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The Employee <select>'s options are keyed by team_members.id (see the
  // `employees` prop — GET /settings/shifts/team-members), which is NOT the
  // same id space as `userId` (a users.id, defaulting to `myId`). Kept separate
  // so the manager lookup below always gets the right kind of id.
  const [employeeMemberId, setEmployeeMemberId] = useState("");
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  const handleEmployeeChange = (e) => {
    const val = e.target.value;
    setEmployeeMemberId(val);
    setUserId(val);
    setManagerId("");
  };

  /* ── Days that need attention ──────────────────────────────────────── */
  const loadDays = useCallback(() => {
    setLoadingDays(true);
    axios
      .get(`${API_URL}/api/attendance/regularise/gaps`, {
        ...auth(),
        params: { month, ...(employeeMemberId ? { employee_id: employeeMemberId } : {}) },
      })
      .then((r) => {
        const data = r.data || {};
        setShift(data.shift || { start: "09:30", end: "18:30" });
        setPendingDates(new Set(data.pending_dates || []));
        setRows(
          (data.days || []).map((d) => ({
            ...d,
            key: d.date,
            // Days already raised can't be selected — the request exists.
            selected: !d.already_raised,
            in: d.suggested_in,
            out: d.suggested_out,
          })),
        );
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.clear();
          window.location.assign("/login");
          return;
        }
        setRows([]);
      })
      .finally(() => setLoadingDays(false));
  }, [month, employeeMemberId]);

  useEffect(() => { loadDays(); }, [loadDays]);

  // Candidate managers for whoever the request is currently for.
  useEffect(() => {
    let alive = true;
    setLoadingManagers(true);
    axios
      .get(`${API_URL}/api/attendance/regularise/managers`, {
        ...auth(),
        params: employeeMemberId ? { employee_id: employeeMemberId } : {},
      })
      .then((r) => alive && setManagers(r.data || []))
      .catch(() => alive && setManagers([]))
      .finally(() => alive && setLoadingManagers(false));
    return () => { alive = false; };
  }, [employeeMemberId]);

  /* ── Row helpers ───────────────────────────────────────────────────── */
  const selectable = rows.filter((r) => !r.already_raised);
  const selected = rows.filter((r) => r.selected && !r.already_raised);
  const allSelected = selectable.length > 0 && selected.length === selectable.length;
  const someSelected = selected.length > 0 && !allSelected;

  // Rows are addressed by `key`, not date: a day added by hand starts with no
  // date, and two of those would otherwise be indistinguishable.
  const patchRow = (key, patch) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setError(null);
  };

  const toggleAll = () => {
    const next = !allSelected;
    setRows((prev) => prev.map((r) => (r.already_raised ? r : { ...r, selected: next })));
  };

  const manualSeq = useRef(0);
  const addManualDay = () => {
    // Starts with no date — the user picks the day themselves. It sits at the
    // end of the list until they do (see byDate).
    manualSeq.current += 1;
    setError(null);
    setRows((prev) => [
      ...prev,
      {
        key: `manual-${manualSeq.current}`,
        date: "",
        issue: "manual",
        recorded_in: null,
        recorded_out: null,
        suggested_in: shift.start,
        suggested_out: shift.end,
        already_raised: null,
        manual: true,
        selected: true,
        in: shift.start,
        out: shift.end,
      },
    ]);
  };

  const removeRow = (key) =>
    setRows((prev) => prev.filter((r) => r.key !== key));

  // Copy the first selected row's times onto every other selected row — the
  // common case is one correction repeated across several days.
  const applyTimesToAll = () => {
    if (!selected.length) {
      setError("Select at least one day first.");
      return;
    }
    const { in: first, out } = selected[0];
    setRows((prev) =>
      prev.map((r) => (r.selected && !r.already_raised ? { ...r, in: first, out } : r)),
    );
  };

  const changeManualDate = (key, nextDate) => {
    const listed = nextDate && rows.find((r) => r.date === nextDate && r.key !== key);
    // Only a request still awaiting a decision blocks the date. A day whose
    // earlier request was approved or rejected can be regularised again.
    if (nextDate && (pendingDates.has(nextDate) || listed?.already_raised)) {
      setError(
        `A regularisation for ${dayLabel(nextDate)} is already pending approval — wait for it to be approved or rejected before raising it again.`,
      );
      return;
    }
    // Already in the list and free to raise — use that row rather than adding
    // the same date twice.
    if (listed) {
      setRows((prev) =>
        prev
          .filter((r) => r.key !== key)
          .map((r) => (r.key === listed.key ? { ...r, selected: true } : r)),
      );
      setError(null);
      toast.info(`${dayLabel(nextDate)} is already in the list — it's been selected for you.`);
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, date: nextDate || "" } : r)).sort(byDate),
    );
    setError(null);
  };

  const today = dayKey(new Date());

  /* ── Submit ────────────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selected.length) {
      setError("Select at least one day to regularise.");
      return;
    }
    if (selected.some((r) => !r.date)) {
      setError("Pick a date for the day you added, or remove it.");
      return;
    }
    if (!managerId) {
      setError("Select the manager who should approve this.");
      return;
    }
    if (!reason.trim()) {
      setError("A reason is required.");
      return;
    }
    const bad = selected.find((r) => !r.in || !r.out || r.out <= r.in);
    if (bad) {
      setError(`Check-out must be after check-in on ${dayLabel(bad.date)}.`);
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/attendance/regularise`,
        {
          user_id: userId,
          manager_id: managerId,
          reason: reason.trim(),
          days: selected.map((r) => ({
            date: r.date,
            requested_in: r.in,
            requested_out: r.out,
          })),
        },
        auth(),
      );
      const { created = [], skipped = [] } = res.data || {};
      toast.success(
        `${created.length} day${created.length === 1 ? "" : "s"} submitted — awaiting approval.`,
      );
      if (skipped.length) {
        toast.warning(
          `${skipped.length} skipped: ${skipped.map((s) => dayLabel(s.date)).join(", ")} already ${skipped.length === 1 ? "has" : "have"} a regularisation pending approval.`,
        );
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.clear();
        window.location.assign("/login");
        return;
      }
      setError(err?.response?.data?.error || "Could not submit request.");
    } finally {
      setSaving(false);
    }
  };

  const needAttention = useMemo(
    () => rows.filter((r) => !r.manual && !r.already_raised).length,
    [rows],
  );

  const cell = { padding: "10px 12px", verticalAlign: "middle" };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,2,19,0.45)",
        // .main-header is a sticky element pinned at z-index: 1000 — 1200
        // clears it outright, matching the shared .modal-overlay class.
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(620px, 100%)",
          maxHeight: "calc(100vh - 40px)",
          display: "flex",
          flexDirection: "column",
          background: T.bgCard,
          borderRadius: T.rXl,
          boxShadow: T.shadowMd,
          overflow: "hidden",
        }}
      >
        {/* ── Header (fixed, doesn't scroll) ─────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            padding: "20px 22px",
            borderBottom: `1px solid ${T.divider}`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: T.rLg,
              background: T.orange50,
              color: T.orange,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CalendarClock size={19} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: `700 16px/1.3 ${T.font}`, color: T.fg1 }}>
              Regularise attendance
            </div>
            <div style={{ font: `400 12.5px/1.5 ${T.font}`, color: T.fg5, marginTop: 3 }}>
              Select the days that need correcting. Times come from your shift — edit any that differ.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30,
              height: 30,
              borderRadius: T.rMd,
              border: "none",
              background: "transparent",
              color: T.fg5,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <X size={17} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <div style={{ overflowY: "auto", padding: "18px 22px", flex: 1, minHeight: 0 }}>
            {error && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "10px 12px",
                  background: T.danger50,
                  border: `1px solid rgba(231,0,11,0.25)`,
                  borderRadius: T.rMd,
                  color: T.danger,
                  font: `500 12.5px/1.4 ${T.font}`,
                }}
              >
                ⚠ {error}
              </div>
            )}

            {/* Admin + HR can submit on behalf of any employee */}
            {isManager && employees.length > 0 && (
              <div style={field}>
                <Label>Employee</Label>
                <Select value={employeeMemberId} onChange={handleEmployeeChange}>
                  <option value="">Myself</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {/* ── Month navigator + count ──────────────────────────── */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 14,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  border: `1px solid ${T.border}`,
                  borderRadius: 999,
                  padding: "4px 6px",
                }}
              >
                <button
                  type="button"
                  aria-label="Previous month"
                  onClick={() => setMonth((m) => shiftMonth(m, -1))}
                  style={{ ...linkBtn, color: T.fg5, display: "flex", padding: 4 }}
                >
                  <ChevronLeft size={15} />
                </button>
                <span style={{ font: `600 13px ${T.font}`, color: T.fg1, minWidth: 120, textAlign: "center" }}>
                  {monthLabel(month)}
                </span>
                <button
                  type="button"
                  aria-label="Next month"
                  disabled={month >= monthKey(new Date())}
                  onClick={() => setMonth((m) => shiftMonth(m, 1))}
                  style={{
                    ...linkBtn,
                    color: month >= monthKey(new Date()) ? T.border : T.fg5,
                    cursor: month >= monthKey(new Date()) ? "default" : "pointer",
                    display: "flex",
                    padding: 4,
                  }}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
              <span style={{ flex: 1 }} />
              {!loadingDays && (
                <span
                  style={{
                    font: `600 12px ${T.font}`,
                    color: needAttention ? T.orange : T.fg5,
                    background: needAttention ? T.orange50 : "transparent",
                    borderRadius: 999,
                    padding: needAttention ? "5px 11px" : 0,
                  }}
                >
                  {needAttention
                    ? `${needAttention} day${needAttention === 1 ? "" : "s"} need attention`
                    : "Nothing flagged this month"}
                </span>
              )}
            </div>

            {/* ── Day table ────────────────────────────────────────── */}
            <div
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: T.rLg,
                overflow: "hidden",
                marginBottom: 12,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: T.bgMuted }}>
                    <th style={{ ...cell, width: 38 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all days"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={toggleAll}
                        disabled={!selectable.length}
                      />
                    </th>
                    <th style={{ ...cell, textAlign: "left", font: `700 10.5px ${T.font}`, color: T.fg5, letterSpacing: "0.06em" }}>
                      DAY
                    </th>
                    <th style={{ ...cell, textAlign: "left", width: 110, font: `700 10.5px ${T.font}`, color: T.fg5, letterSpacing: "0.06em" }}>
                      CHECK-IN
                    </th>
                    <th style={{ ...cell, textAlign: "left", width: 110, font: `700 10.5px ${T.font}`, color: T.fg5, letterSpacing: "0.06em" }}>
                      CHECK-OUT
                    </th>
                    <th style={{ ...cell, width: 34 }} />
                  </tr>
                </thead>
                <tbody>
                  {loadingDays && (
                    <tr>
                      <td colSpan={5} style={{ ...cell, textAlign: "center", color: T.fg5, font: `400 13px ${T.font}` }}>
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!loadingDays && rows.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ ...cell, textAlign: "center", color: T.fg5, font: `400 13px ${T.font}`, padding: "22px 12px" }}>
                        No gaps found in {monthLabel(month)}.
                        <br />
                        Use “Add a day not listed” to correct a day anyway.
                      </td>
                    </tr>
                  )}
                  {!loadingDays && rows.map((r) => {
                    const locked = !!r.already_raised;
                    const active = r.selected && !locked;
                    return (
                      <tr
                        key={r.key}
                        style={{
                          borderTop: `1px solid ${T.divider}`,
                          background: active ? "rgba(255,105,0,0.06)" : "transparent",
                          opacity: locked ? 0.55 : 1,
                        }}
                      >
                        <td style={cell}>
                          <input
                            type="checkbox"
                            aria-label={r.date ? `Select ${dayLabel(r.date)}` : "Select the added day"}
                            checked={active}
                            disabled={locked}
                            onChange={() => patchRow(r.key, { selected: !r.selected })}
                          />
                        </td>
                        <td style={cell}>
                          {r.manual ? (
                            <Input
                              type="date"
                              value={r.date}
                              max={today}
                              required
                              autoFocus={!r.date}
                              onChange={(e) => changeManualDate(r.key, e.target.value)}
                              style={{ padding: "6px 8px", font: `600 12.5px ${T.font}` }}
                            />
                          ) : (
                            <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>
                              {dayLabel(r.date)}
                            </div>
                          )}
                          <div style={{ font: `400 11.5px ${T.font}`, color: locked ? T.fg5 : T.orange, marginTop: 2 }}>
                            {locked
                              ? "Already pending approval"
                              : r.previous_status
                                ? `${issueLabel(r)} · previously ${r.previous_status}`
                                : issueLabel(r)}
                          </div>
                        </td>
                        <td style={cell}>
                          <Input
                            type="time"
                            value={r.in || ""}
                            disabled={!active}
                            onChange={(e) => patchRow(r.key, { in: e.target.value })}
                            style={{ padding: "6px 8px" }}
                          />
                        </td>
                        <td style={cell}>
                          <Input
                            type="time"
                            value={r.out || ""}
                            disabled={!active}
                            onChange={(e) => patchRow(r.key, { out: e.target.value })}
                            style={{ padding: "6px 8px" }}
                          />
                        </td>
                        <td style={cell}>
                          {r.manual && (
                            <button
                              type="button"
                              aria-label={r.date ? `Remove ${dayLabel(r.date)}` : "Remove the added day"}
                              onClick={() => removeRow(r.key)}
                              style={{ ...linkBtn, color: T.danger, display: "flex" }}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
              <button type="button" onClick={addManualDay} style={{ ...linkBtn, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Plus size={13} /> Add a day not listed
              </button>
              <span style={{ color: T.border }}>|</span>
              <button type="button" onClick={applyTimesToAll} style={{ ...linkBtn, color: T.fg2 }}>
                Set the same times for all selected
              </button>
            </div>

            <div style={field}>
              <Label>Manager *</Label>
              <Select
                value={managerId}
                onChange={(e) => { setManagerId(e.target.value); setError(null); }}
                disabled={loadingManagers}
              >
                <option value="">
                  {loadingManagers ? "Loading…" : "Select a manager"}
                </option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.source === "assigned" ? " — your manager" : m.project_name ? ` — ${m.project_name}` : ""}
                  </option>
                ))}
              </Select>
              {/* Without a candidate there is nobody to route this to, and the
                  form cannot be submitted — say so here rather than only on
                  the failed submit. */}
              {!loadingManagers && managers.length === 0 && (
                <div style={{ font: `400 11.5px/1.5 ${T.font}`, color: T.danger, marginTop: 5 }}>
                  No manager is set for this employee. Ask HR to set a Primary Manager
                  on the Employment tab, or add them to a project that has one.
                </div>
              )}
            </div>

            <div style={{ marginBottom: 4 }}>
              <Label>Reason *</Label>
              <textarea
                value={reason}
                onChange={(e) => { setReason(e.target.value); setError(null); }}
                rows={3}
                placeholder="Briefly explain why attendance was not marked (e.g. forgot to clock in, device issue…)"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: `1px solid ${T.border}`,
                  borderRadius: T.rMd,
                  background: "var(--em-bg-card)",
                  font: `400 13px/1.5 ${T.font}`,
                  color: T.fg1,
                  outline: "none",
                  boxSizing: "border-box",
                  resize: "vertical",
                  minHeight: 72,
                }}
              />
              <div style={{ font: `400 11.5px ${T.font}`, color: T.fg5, marginTop: 5 }}>
                The same reason is recorded against every day you selected.
              </div>
            </div>
          </div>

          {/* ── Footer (fixed, doesn't scroll) ───────────────────────── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "14px 22px",
              borderTop: `1px solid ${T.divider}`,
              flexShrink: 0,
            }}
          >
            <span style={{ flex: 1, font: `400 12px ${T.font}`, color: T.fg5 }}>
              {selected.length} of {selectable.length} day{selectable.length === 1 ? "" : "s"} selected
            </span>
            <SecondaryButton type="button" onClick={onClose} disabled={saving}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={saving || !selected.length}>
              {saving
                ? "Submitting…"
                : `Submit ${selected.length || ""} ${selected.length === 1 ? "day" : "days"}`.replace(/\s+/g, " ").trim()}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};

RegularisationModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  employees: PropTypes.array,
};

export default RegularisationModal;
